import { parseMenuFlatPayload } from "./menuFlatCore.js";

function sortByPosition(items = []) {
  return [...items]
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const isAOther =
        Number(a.item?.is_flat_out_of_course) === 1 ||
        (Number(a.item?.id) === -1 && String(a.item?.name || "").trim() === "");
      const isBOther =
        Number(b.item?.is_flat_out_of_course) === 1 ||
        (Number(b.item?.id) === -1 && String(b.item?.name || "").trim() === "");

      if (isAOther && !isBOther) return 1;
      if (!isAOther && isBOther) return -1;

      const aPosition = Number(a.item?.position);
      const bPosition = Number(b.item?.position);
      const hasAPosition = Number.isFinite(aPosition);
      const hasBPosition = Number.isFinite(bPosition);

      if (hasAPosition && hasBPosition && aPosition !== bPosition) return aPosition - bPosition;
      if (hasAPosition && !hasBPosition) return -1;
      if (!hasAPosition && hasBPosition) return 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

function cloneEntity(entity) {
  if (!entity) return null;
  if (typeof structuredClone === "function") return structuredClone(entity);
  return JSON.parse(JSON.stringify(entity));
}

function getLegacyBucket(relation, entityType) {
  const candidates = [
    relation?.legacy_bucket,
    relation?.bucket,
    relation?.column,
    relation?.column_type,
    relation?.side,
    relation?.slot,
  ];

  const normalized = candidates.find((value) => value !== undefined && value !== null);

  if (normalized === 1 || normalized === "1" || normalized === "left" || normalized === "primary") {
    return entityType === "variant" ? "variants" : "products";
  }

  if (
    normalized === 2 ||
    normalized === "2" ||
    normalized === "right" ||
    normalized === "secondary"
  ) {
    return entityType === "variant" ? "variants2" : "products2";
  }

  return entityType === "variant" ? "variants2" : "products2";
}

function buildProductAllergens(parsed, productId) {
  const productRelations = parsed.relationIndex.productAllergensByProductId[productId] || [];
  if (productRelations.length === 0) return [];

  const relationAllergenIds = new Set(
    productRelations
      .map((relation) => Number(relation?.allergen_id))
      .filter((allergenId) => Number.isFinite(allergenId)),
  );

  return (parsed.entities.allergens || [])
    .filter((allergen) => relationAllergenIds.has(Number(allergen?.id)))
    .map((allergen) => cloneEntity(allergen));
}

function relationMatchesVariantCategoryAttachment(relation, productAttachment, categoryAttachment) {
  if (
    relation?.products_variants_categories_relations_id !== undefined &&
    relation?.products_variants_categories_relations_id !== null
  ) {
    return (
      productAttachment &&
      relation.products_variants_categories_relations_id === productAttachment.id
    );
  }

  if (
    relation?.categories_variants_categories_relations_id !== undefined &&
    relation?.categories_variants_categories_relations_id !== null
  ) {
    return (
      categoryAttachment &&
      relation.categories_variants_categories_relations_id === categoryAttachment.id
    );
  }

  return true;
}

function getPreferredVariantCategoryRelations(relations = [], attachment, variantCategoryId) {
  const productAttachmentId = attachment?.productAttachment?.id ?? null;
  const categoryAttachmentId = attachment?.categoryAttachment?.id ?? null;

  if (productAttachmentId !== null) {
    const productSpecificRelations = relations.filter(
      (relation) => relation?.products_variants_categories_relations_id === productAttachmentId,
    );
    if (productSpecificRelations.length > 0) return productSpecificRelations;
  }

  if (categoryAttachmentId !== null) {
    const categorySpecificRelations = relations.filter(
      (relation) =>
        relation?.categories_variants_categories_relations_id === categoryAttachmentId,
    );
    if (categorySpecificRelations.length > 0) return categorySpecificRelations;
  }

  return relations.filter((relation) => relation?.variant_category_id === variantCategoryId);
}

function buildIngredient(parsed, ingredientId) {
  const ingredient = cloneEntity(parsed.byId.ingredients[ingredientId]);
  if (!ingredient) return null;
  ingredient.assortment = parsed.assortmentIndex.ingredientsByIngredientId[ingredient.id] || [];
  return ingredient;
}

function buildProductBase(parsed, productId) {
  const product = cloneEntity(parsed.byId.products[productId]);
  if (!product) return null;

  product.prices = parsed.priceGroups.productsByProductId[product.id] || [];
  product.assortment = parsed.assortmentIndex.productsByProductId[product.id] || [];
  product.tags =
    (parsed.relationIndex.tagsProductsByProductId[product.id] || [])
      .map((relation) => cloneEntity(parsed.byId.tags[relation.tag_id]))
      .filter(Boolean) || [];
  product.ingredients =
    (parsed.relationIndex.productIngredientsByProductId[product.id] || [])
      .map((relation) => buildIngredient(parsed, relation.ingredient_id))
      .filter(Boolean) || [];
  product.allergens = buildProductAllergens(parsed, product.id);
  product.limit_groups =
    (parsed.relationIndex.productsLimitGroupsByProductId[product.id] || [])
      .map((relation) => {
        const limitGroup = cloneEntity(parsed.byId.limit_groups[relation.limit_group_id]);
        if (!limitGroup) return null;
        limitGroup.lists =
          (parsed.relationIndex.listsLimitGroupsByLimitGroupId[limitGroup.id] || [])
            .map((listRelation) => listRelation.list_id)
            .filter((id) => id != null) || [];
        return limitGroup;
      })
      .filter(Boolean) || [];

  return product;
}

function buildVariantCategoryInstance(parsed, attachment) {
  const variantCategory = cloneEntity(attachment?.variantCategory);
  if (!variantCategory) return null;
  const sourceAttachment = attachment?.productAttachment || attachment?.categoryAttachment || {};
  const isIngredientsCategory = Number(variantCategory.is_ingredients) === 1;

  const variantRelations = getPreferredVariantCategoryRelations(
    parsed.relations.variants_categories_variants || [],
    attachment,
    variantCategory.id,
  );
  const productRelations = getPreferredVariantCategoryRelations(
    parsed.relations.variants_categories_products || [],
    attachment,
    variantCategory.id,
  );

  variantCategory.variants = [];
  variantCategory.variants2 = [];
  variantCategory.products = [];
  variantCategory.products2 = [];
  variantCategory.selected = 0;

  if (sourceAttachment.min !== undefined && sourceAttachment.min !== null) {
    variantCategory.min = Number(sourceAttachment.min);
  }
  if (sourceAttachment.max !== undefined && sourceAttachment.max !== null) {
    variantCategory.max = Number(sourceAttachment.max);
  }
  if (sourceAttachment.force_mono !== undefined && sourceAttachment.force_mono !== null) {
    variantCategory.force_mono = Number(sourceAttachment.force_mono);
  }
  if (isIngredientsCategory) {
    variantCategory.force_mono = 1;
  }

  if (!Number.isFinite(Number(variantCategory.min))) variantCategory.min = 0;
  if (!Number.isFinite(Number(variantCategory.max))) variantCategory.max = 999;
  if (!Number.isFinite(Number(variantCategory.force_mono))) variantCategory.force_mono = 0;
  if (!Number.isFinite(Number(variantCategory.has_price))) variantCategory.has_price = 0;

  for (const relation of variantRelations) {
    if (
      !relationMatchesVariantCategoryAttachment(
        relation,
        attachment.productAttachment,
        attachment.categoryAttachment,
      )
    ) {
      continue;
    }

    const variant = cloneEntity(parsed.byId.variants[relation.variant_id]);
    if (!variant) continue;

    variant.prices = parsed.priceGroups.variantsByVariantId[variant.id] || [];
    variant.assortment = parsed.assortmentIndex.variantsByVariantId[variant.id] || [];
    variant.is_default = isIngredientsCategory ? 1 : relation.is_default ? Number(relation.is_default) : 0;
    variant.removable = relation.removable !== undefined ? Number(relation.removable) : 1;
    variant.products_variants_categories_relations_id =
      relation.products_variants_categories_relations_id ?? null;
    variant.categories_variants_categories_relations_id =
      relation.categories_variants_categories_relations_id ?? null;

    variantCategory[getLegacyBucket(relation, "variant")].push(variant);
  }

  for (const relation of productRelations) {
    if (
      !relationMatchesVariantCategoryAttachment(
        relation,
        attachment.productAttachment,
        attachment.categoryAttachment,
      )
    ) {
      continue;
    }

    const product = buildProductBase(parsed, relation.product_id);
    if (!product) continue;

    product.is_default = isIngredientsCategory ? 1 : relation.is_default ? Number(relation.is_default) : 0;
    product.removable = relation.removable !== undefined ? Number(relation.removable) : 1;
    product.products_variants_categories_relations_id =
      relation.products_variants_categories_relations_id ?? null;
    product.categories_variants_categories_relations_id =
      relation.categories_variants_categories_relations_id ?? null;
    product.variants_categories = [];

    variantCategory[getLegacyBucket(relation, "product")].push(product);
  }

  variantCategory.variants = sortByPosition(variantCategory.variants);
  variantCategory.variants2 = sortByPosition(variantCategory.variants2);
  variantCategory.products = sortByPosition(variantCategory.products);
  variantCategory.products2 = sortByPosition(variantCategory.products2);

  return variantCategory;
}

function buildProductVariantCategories(parsed, productId, categoryId = null) {
  const directAttachments = parsed.relationIndex.productVariantCategoriesByProductId[productId] || [];
  const categoryIds = categoryId
    ? [categoryId]
    : (parsed.relationIndex.categoryIdsByProductId[productId] || []).map(
        (relation) => relation.category_id,
      );
  const attachments = [];
  const seen = new Set();

  for (const relation of directAttachments) {
    const key = `product:${relation.id ?? relation.variant_category_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    attachments.push({
      productAttachment: relation,
      categoryAttachment: null,
      variantCategory: parsed.byId.variants_categories[relation.variant_category_id],
    });
  }

  for (const currentCategoryId of categoryIds) {
    const categoryAttachments =
      parsed.relationIndex.categoryVariantCategoriesByCategoryId[currentCategoryId] || [];

    for (const relation of categoryAttachments) {
      const key = `category:${relation.id ?? relation.variant_category_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      attachments.push({
        productAttachment: null,
        categoryAttachment: relation,
        variantCategory: parsed.byId.variants_categories[relation.variant_category_id],
      });
    }
  }

  return sortByPosition(
    attachments
      .map((attachment) => buildVariantCategoryInstance(parsed, attachment))
      .filter((variantCategory) => variantCategory !== null),
  );
}

function buildProductView(parsed, productId, categoryId = null) {
  const product = buildProductBase(parsed, productId);
  if (!product) return null;

  product.category_id = categoryId;
  product.variants_categories = buildProductVariantCategories(parsed, productId, categoryId);

  return product;
}

export function adaptMenuFlat(parsedOrPayload) {
  const parsed =
    parsedOrPayload?.byId && parsedOrPayload?.relationIndex
      ? parsedOrPayload
      : parseMenuFlatPayload(parsedOrPayload);

  const categoriesById = {};
  const courses = [];
  const outOfCourseCategories = [];

  for (const category of parsed.entities.categories) {
    const categoryView = cloneEntity(category);
    categoryView.tags =
      (parsed.relationIndex.tagsCategoriesByCategoryId[category.id] || [])
        .map((relation) => cloneEntity(parsed.byId.tags[relation.tag_id]))
        .filter(Boolean) || [];
    categoryView.products = sortByPosition(
      (parsed.relationIndex.categoryProductsByCategoryId[category.id] || [])
        .map((relation) => buildProductView(parsed, relation.product_id, category.id))
        .filter(Boolean),
    );
    categoriesById[category.id] = categoryView;
  }

  for (const course of sortByPosition(parsed.entities.courses)) {
    const courseView = cloneEntity(course);
    courseView.categories = sortByPosition(
      (parsed.relationIndex.courseCategoriesByCourseId[course.id] || [])
        .map((relation) => cloneEntity(categoriesById[relation.category_id]))
        .filter(Boolean),
    );
    courses.push(courseView);
  }

  for (const relation of parsed.relations.course_categories) {
    if (Number(relation.course_id) !== -1) continue;
    const category = cloneEntity(categoriesById[relation.category_id]);
    if (category) outOfCourseCategories.push(category);
  }

  if (outOfCourseCategories.length > 0) {
    courses.push({
      id: -1,
      name: "",
      position: 999999,
      categories: sortByPosition(outOfCourseCategories),
      is_flat_out_of_course: 1,
    });
  }

  return {
    version: parsed.version,
    menu: sortByPosition(courses),
    lists: sortByPosition(parsed.entities.lists),
    categoriesById,
    parsed,
  };
}
